import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Pricing-redesign: FxRate admin client.
 *
 * Used by quote composer to convert non-CAD external carrier rates to CAD.
 * Latest effective row for (fromCurrency, toCurrency) wins; older rows stay
 * around for audit. No isActive flag.
 */
export interface FxRate {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  rate: string | number; // Prisma Decimal — serialised as string over JSON, but seeds may emit number
  effectiveAt: string;
  source: string;
  createdAt: string;
}

export interface CreateFxRateInput {
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  effectiveAt: string;
  source?: string;
}

export interface UpdateFxRateInput {
  rate?: number;
  source?: string;
}

export function useGetFxRates(params?: {
  fromCurrency?: string;
  toCurrency?: string;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["fx-rates", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.fromCurrency) q.append("fromCurrency", params.fromCurrency);
      if (params?.toCurrency) q.append("toCurrency", params.toCurrency);

      const { data } = await axios.get<{
        success: boolean;
        data: FxRate[];
      }>(
        `${API_URL}/api/v2/admin/fx-rates${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreateFxRate(options?: {
  onSuccess?: (data: FxRate) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateFxRateInput) => {
      const { data } = await axios.post<{ success: boolean; data: FxRate }>(
        `${API_URL}/api/v2/admin/fx-rates`,
        input,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["fx-rates"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateFxRate(options?: {
  onSuccess?: (data: FxRate) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdateFxRateInput;
    }) => {
      const { data } = await axios.patch<{ success: boolean; data: FxRate }>(
        `${API_URL}/api/v2/admin/fx-rates/${id}`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["fx-rates"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteFxRate(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/admin/fx-rates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fx-rates"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function formatRate(rate: string | number): string {
  return Number(rate).toFixed(4);
}
